/**
 * Pagina Scadenzario - dashboard scadenze di pagamento/incasso.
 *
 * Mostra riepilogo importi e lista scadenze con filtri per anno,
 * stato e direzione. Il parametro anno viene sempre inviato all'API
 * per sfruttare il partizionamento PostgreSQL.
 *
 * Task 19 (cantiere contratti passivi, criterio di collaudo "le ritrova nello
 * Scadenzario, col fornitore"):
 * - colonna Fornitore con la ragione sociale (dal LEFT JOIN soggetti del
 *   backend);
 * - conteggio vero dell'insieme filtrato + paginazione esplicita, invece di
 *   troncare a 50 righe in silenzio;
 * - filtri anno/stato/direzione nella URL (useUrlState), cosi' sopravvivono
 *   al back e al refresh;
 * - date in formato it-IT e importi allineati a destra con tabular-nums.
 *
 * Task 10 (cantiere contratti attivi, BL-114): la colonna diventa
 * "Controparte" (il soggetto e' un cliente sulle attive, non piu' solo un
 * fornitore); il tipo 'contratto_attivo' entra fra le ATTIVE nella
 * derivazione client della direzione; etichette leggibili per i due tipi
 * 'contratto_*' nella colonna Tipo.
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@floraltech/ui';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useUrlNumberState, useUrlState } from '@lib/useUrlState';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface DashboardData {
  da_pagare: number;
  da_incassare: number;
  scadute_passive: number;
  scadute_attive: number;
}

interface Scadenza {
  id: string;
  data_scadenza: string | null;
  data_chiusura: string | null;
  tipo: string;
  descrizione: string | null;
  importo: number | null;
  stato: 'aperta' | 'chiusa';
  soggetto_id: string | null;
  documento_id: string | null;
  ragione_sociale: string | null;
  scaduta: boolean;
}

interface ListaScadenzeResponse {
  scadenze: Scadenza[];
  totale: number;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const ANNO_CORRENTE = new Date().getFullYear();
const DIMENSIONE_PAGINA_DEFAULT = 50;
// MAX_PAGE_SIZE del backend e' 200 (scadenzario.py, Query(50, le=200)):
// non offrire scelte che verrebbero rifiutate.
const DIMENSIONI_PAGINA = [25, 50, 100, 200];

// Stesso elenco positivo di TIPI_SCADENZA_PASSIVI in scadenzario.py: la
// direzione si deriva dal tipo, l'API non porta un campo "direzione" a se'.
// 'contratto_attivo' (Task 6, BL-114) NON va qui apposta: cadendo fuori da
// questo elenco finisce nel ramo 'attiva' di default, che e' dove deve
// stare - stessa collocazione di TIPI_SCADENZA_ATTIVI in scadenzario.py,
// l'elenco gemello mantenuto a mano sul backend. Se in futuro nasce un
// nuovo tipo, verificare entrambi gli elenchi prima di aggiungerlo qui.
const TIPI_PASSIVI = new Set(['fattura_passiva', 'contratto_passivo']);
const direzioneDiTipo = (tipo: string): 'attiva' | 'passiva' =>
  TIPI_PASSIVI.has(tipo) ? 'passiva' : 'attiva';

// Etichette leggibili per i tipi 'contratto_*': 'fattura_attiva' e
// 'fattura_passiva' restano leggibili cosi' come sono, questi due no.
// Stesso pattern di etichettaTipo in ArchivioList.tsx: fallback al codice
// grezzo se il tipo non e' mappato.
const ETICHETTE_TIPO: Record<string, string> = {
  contratto_passivo: 'Canone da pagare',
  contratto_attivo: 'Canone da incassare',
};
const etichettaTipo = (tipo: string): string => ETICHETTE_TIPO[tipo] ?? tipo;

const formatEUR = (value: number | null): string =>
  value == null
    ? '-'
    : new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);

// Data composta dalle parti locali (split), MAI new Date(stringa ISO): quella
// sintassi ECMA-262 e' mezzanotte UTC, e un runtime a offset negativo
// mostrerebbe il giorno sbagliato - stessa classe del bug gia' corretto su
// fmtDate/oggiISO nel modulo contratti (review controllore finale BL-077,
// Important I6: qui e' rientrato con il Task 19).
const formatDataIT = (iso: string | null): string => {
  if (!iso) return '-';
  const soloData = iso.split('T')[0];
  const [anno, mese, giorno] = soloData.split('-').map(Number);
  if (!anno || !mese || !giorno) return '-';
  return new Date(anno, mese - 1, giorno).toLocaleDateString('it-IT');
};

const authHeaders = (): HeadersInit => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const Scadenzario: React.FC = () => {
  /* --- State: dashboard ----------------------------------------------- */
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [dashLoading, setDashLoading] = useState(true);

  /* --- State: lista scadenze ----------------------------------------- */
  const [scadenze, setScadenze] = useState<Scadenza[]>([]);
  const [totale, setTotale] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /* --- State: filtri, nella URL cosi' sopravvivono al back/refresh --- */
  const [anno, setAnno] = useUrlNumberState('anno', ANNO_CORRENTE);
  const [stato, setStato] = useUrlState('stato', '__all__');
  const [direzione, setDirezione] = useUrlState('direzione', '__all__');

  /* --- State: paginazione, nella URL come i filtri -------------------- */
  const [pagina, setPagina] = useUrlNumberState('p', 1);
  const [perPagina, setPerPagina] = useUrlNumberState('n', DIMENSIONE_PAGINA_DEFAULT);

  /* --- Fetch dashboard ----------------------------------------------- */
  const caricaDashboard = useCallback(async () => {
    setDashLoading(true);
    try {
      const res = await fetch(
        `/api/v1/scadenzario/dashboard?anno=${anno}`,
        { headers: authHeaders() },
      );
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      const data: DashboardData = await res.json();
      setDashboard(data);
    } catch {
      setDashboard(null);
    } finally {
      setDashLoading(false);
    }
  }, [anno]);

  /* --- Firma dei filtri (paginazione esclusa): se cambia si torna a
     pagina 1, altrimenti si resterebbe su una pagina che nel nuovo
     insieme non esiste. --- */
  const firmaFiltri = [anno, stato, direzione].join('|');
  const firmaPrecedente = useRef(firmaFiltri);
  useEffect(() => {
    if (firmaPrecedente.current !== firmaFiltri) {
      firmaPrecedente.current = firmaFiltri;
      if (pagina !== 1) setPagina(1);
    }
  }, [firmaFiltri, pagina, setPagina]);

  /* --- Fetch lista --------------------------------------------------- */
  const caricaScadenze = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ anno: String(anno) });
      if (stato !== '__all__') params.set('stato', stato);
      if (direzione !== '__all__') params.set('direzione', direzione);
      params.set('limit', String(perPagina));
      params.set('offset', String(Math.max(0, (pagina - 1) * perPagina)));

      const res = await fetch(
        `/api/v1/scadenzario/?${params.toString()}`,
        { headers: authHeaders() },
      );
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      const data: ListaScadenzeResponse = await res.json();
      setScadenze(data.scadenze);
      setTotale(data.totale);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
      setTotale(null);
    } finally {
      setLoading(false);
    }
  }, [anno, stato, direzione, pagina, perPagina]);

  /* --- Effects ------------------------------------------------------- */
  useEffect(() => {
    void caricaDashboard();
  }, [caricaDashboard]);

  useEffect(() => {
    void caricaScadenze();
  }, [caricaScadenze]);

  /* --- Dashboard cards config ---------------------------------------- */
  const dashCards: { label: string; key: keyof DashboardData; color: string }[] = [
    { label: 'Da pagare', key: 'da_pagare', color: 'text-orange-600' },
    { label: 'Da incassare', key: 'da_incassare', color: 'text-blue-600' },
    { label: 'Scadute passive', key: 'scadute_passive', color: 'text-red-600' },
    { label: 'Scadute attive', key: 'scadute_attive', color: 'text-red-500' },
  ];

  const ultimaPagina = totale === null ? pagina : Math.max(1, Math.ceil(totale / perPagina));
  const primoDellaPagina = totale === 0 ? 0 : (pagina - 1) * perPagina + 1;
  const ultimoDellaPagina = (pagina - 1) * perPagina + scadenze.length;

  /* ------------------------------------------------------------------ */
  /*  Render                                                             */
  /* ------------------------------------------------------------------ */

  return (
    <div className="space-y-6 p-6">
      {/* Titolo */}
      <h1 className="font-outfit text-2xl font-semibold text-neutral-dark dark:text-neutral-light">
        Scadenzario
      </h1>

      {/* Dashboard cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {dashCards.map((c) => (
          <Card key={c.key}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {c.label}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className={`text-2xl font-bold tabular-nums ${c.color}`}>
                {dashLoading
                  ? '...'
                  : dashboard
                    ? formatEUR(dashboard[c.key])
                    : '-'}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filtri */}
      <Card>
        <CardHeader>
          <CardTitle>Filtri</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-4">
          <Select
            value={String(anno)}
            onValueChange={(v) => setAnno(Number(v))}
          >
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Anno" />
            </SelectTrigger>
            <SelectContent>
              {[ANNO_CORRENTE - 1, ANNO_CORRENTE, ANNO_CORRENTE + 1].map((y) => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={stato} onValueChange={setStato}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Stato" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutti</SelectItem>
              <SelectItem value="aperta">Aperta</SelectItem>
              <SelectItem value="chiusa">Chiusa</SelectItem>
            </SelectContent>
          </Select>

          <Select value={direzione} onValueChange={setDirezione}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Direzione" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutte</SelectItem>
              <SelectItem value="attiva">Attiva</SelectItem>
              <SelectItem value="passiva">Passiva</SelectItem>
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            onClick={() => { void caricaScadenze(); void caricaDashboard(); }}
          >
            Aggiorna
          </Button>
        </CardContent>
      </Card>

      {/* Conteggio dell'insieme filtrato: non e' la somma delle righe caricate,
          e' cio' che il backend dichiara con conta_scadenze (stessa WHERE della
          lista, senza LIMIT). Prima la pagina troncava a 50 in silenzio. */}
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 text-sm text-muted-foreground">
        <span>
          {loading
            ? 'Caricamento...'
            : totale === null
              ? `${scadenze.length} risultati in questa pagina`
              : totale === 0
                ? 'Nessuna scadenza trovata'
                : `${primoDellaPagina}-${ultimoDellaPagina} di ${totale} scadenze`}
        </span>
      </div>

      {/* Tabella scadenze */}
      <Card>
        <CardContent className="p-0">
          {error && (
            <p className="p-6 text-center text-sm text-destructive">{error}</p>
          )}
          {!error && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Scadenza</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Direzione</TableHead>
                  <TableHead>Controparte</TableHead>
                  <TableHead>Descrizione</TableHead>
                  <TableHead className="text-right">Importo</TableHead>
                  <TableHead>Stato</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!loading && scadenze.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="text-center text-sm text-muted-foreground"
                    >
                      Nessuna scadenza trovata
                    </TableCell>
                  </TableRow>
                )}
                {scadenze.map((s) => {
                  const dir = direzioneDiTipo(s.tipo);
                  const scaduta = s.stato === 'aperta' && s.scaduta;
                  return (
                    <TableRow key={s.id}>
                      <TableCell
                        className={scaduta ? 'font-bold text-red-600' : ''}
                      >
                        {formatDataIT(s.data_scadenza)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{etichettaTipo(s.tipo)}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={dir === 'attiva' ? 'default' : 'secondary'}>
                          {dir === 'attiva' ? 'Attiva' : 'Passiva'}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-xs truncate">
                        {s.ragione_sociale ?? '-'}
                      </TableCell>
                      <TableCell className="max-w-xs truncate">
                        {s.descrizione}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatEUR(s.importo)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={s.stato === 'aperta' ? (scaduta ? 'destructive' : 'default') : 'secondary'}
                        >
                          {s.stato === 'aperta'
                            ? scaduta
                              ? 'Scaduta'
                              : 'Aperta'
                            : 'Chiusa'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Paginazione: senza, le scadenze oltre la prima pagina erano
          invisibili e il conteggio taceva sulla loro esistenza. */}
      {!error && (totale === null || totale > 0) && (
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <span>Righe per pagina</span>
            <Select
              value={String(perPagina)}
              onValueChange={(v) => {
                setPerPagina(Number(v));
                setPagina(1);
              }}
            >
              <SelectTrigger className="h-8 w-20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DIMENSIONI_PAGINA.map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={loading || pagina <= 1}
              onClick={() => setPagina(Math.max(1, pagina - 1))}
            >
              <ChevronLeft className="h-4 w-4" />
              Precedente
            </Button>
            <span className="px-1 text-muted-foreground">
              Pagina {pagina}
              {totale !== null && ` di ${ultimaPagina}`}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={
                loading ||
                (totale !== null ? pagina >= ultimaPagina : scadenze.length < perPagina)
              }
              onClick={() => setPagina(pagina + 1)}
            >
              Successiva
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Scadenzario;
